import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Lock, Unlock, LockOpen, Clock, Users, Heart, Star, Gift, ChevronRight } from 'lucide-react';
import { resolveAssetUrl, flag } from '../config';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';

import { getCategoryColors, CATEGORY_LIST } from '../config/businessCategories';
import SEO from '../components/SEO';
import { trackHubUnlock } from '../utils/analytics';

const ITEMS_PER_PAGE = 9; // 3 rows x 3 columns

const Businesses = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedHub, setSelectedHub] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [loading, setLoading] = useState(true);
  // Use fixed main categories from config - always show all 10 categories
  const categories = ['All', ...CATEGORY_LIST];
  const [sortBy, setSortBy] = useState('distance');
  const [notification, setNotification] = useState(null);
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const [featuredDeal, setFeaturedDeal] = useState(null);
  const { isAuthenticated, user, openAuthModal, setPostAuthRedirect, completePendingClaim } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Unlock celebration state

  // Preview mode state (for unauthenticated users who scanned)
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [previewHubId, setPreviewHubId] = useState(null);
  const [previewHubName, setPreviewHubName] = useState(null);

  // Fetch hubs with deal counts
  useEffect(() => {
    const fetchHubs = async () => {
      try {
        const { data } = await api.get('/hubs/with-deals');
        setHubs(data);
      } catch (error) {
        console.error('Error fetching hubs:', error);
      }
    };
    fetchHubs();
  }, []);

  // Auto-select hub from URL parameter (e.g., /local-deals-near-me?hub_id=xxx)
  useEffect(() => {
    const hubIdFromUrl = searchParams.get('hub_id');
    if (hubIdFromUrl && hubs.length > 0) {
      const hubExists = hubs.some(h => String(h.id) === String(hubIdFromUrl));
      if (hubExists) {
        setSelectedHub(hubIdFromUrl);
      }
    }
  }, [searchParams, hubs]);

  // Handle preview mode for unauthenticated users (scan-to-preview flow)
  useEffect(() => {
    const previewParam = searchParams.get('preview');
    const hubIdFromUrl = searchParams.get('hub_id');

    if (previewParam === 'true' && hubIdFromUrl && !isAuthenticated) {
      // Set preview mode
      setIsPreviewMode(true);
      setPreviewHubId(hubIdFromUrl);

      // Get hub name from sessionStorage or hubs array
      const storedHubName = sessionStorage.getItem('preview_hub_name');
      const hubInfo = hubs.find(h => String(h.id) === String(hubIdFromUrl));
      setPreviewHubName(storedHubName || hubInfo?.name || 'this venue');

      // Auto-scroll to deals section
      setTimeout(() => {
        document.getElementById('deals')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);

      // Clean up URL (remove preview param but keep hub_id)
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('preview');
      window.history.replaceState({}, '', newUrl.toString());
    }
  }, [searchParams, hubs, isAuthenticated]);

  // Clear preview mode when user authenticates and complete any pending claims
  useEffect(() => {
    const handlePostAuthPreview = async () => {
      if (isAuthenticated && isPreviewMode) {
        setIsPreviewMode(false);
        setPreviewHubId(null);
        setPreviewHubName(null);
      }

      // Check for pending claim from preview flow (handles Google OAuth case)
      const pendingClaimStr = sessionStorage.getItem('pendingClaim');
      const previewHub = sessionStorage.getItem('preview_hub_id');

      if (isAuthenticated && (pendingClaimStr || previewHub)) {
        try {
          const result = await completePendingClaim();
          if (result?.success && !result.unlockOnly && !result.noPending) {
            if (flag('REWARDS')) {
              setNotification({ type: 'taps', message: '+175 Taps Earned!' });
              setTimeout(() => setNotification(null), 4000);
            }
          }
        } catch (error) {
          console.error('Error completing pending claim:', error);
        }
      }
    };

    handlePostAuthPreview();
  }, [isAuthenticated, isPreviewMode, completePendingClaim]);

  // Fetch the featured deal for the selected hub (Phase 1: no unlock condition)
  useEffect(() => {
    const fetchFeaturedDeal = async () => {
      if (!selectedHub) {
        setFeaturedDeal(null);
        return;
      }
      try {
        const { data } = await api.get(`/locations/${selectedHub}/featured-deal`);
        if (data.featured) {
          const hubInfo = hubs.find(h => h.id === selectedHub);
          setFeaturedDeal({ ...data, hubName: hubInfo?.name || 'Selected Venue' });
        } else {
          setFeaturedDeal(null);
        }
      } catch (err) {
        console.error('Error fetching featured deal:', err);
        setFeaturedDeal(null);
      }
    };
    fetchFeaturedDeal();
  }, [selectedHub, hubs]);

  // Fetch businesses from API
  useEffect(() => {
    const fetchBusinesses = async () => {
      try {
        const { data } = await api.get('/businesses');
        
        const mappedBusinesses = data.businesses.map(b => {
          const firstDeal = b.deals && b.deals.length > 0 ? b.deals[0] : null;
          
          // Get the best available image - check API field names: image_url, logo_url
          let businessImage = null;
          
          // Check image_url first (main business image from API)
          if (b.image_url && b.image_url.trim() !== '') {
            // Handle relative URLs (e.g., /api/files/...)
            businessImage = resolveAssetUrl(b.image_url);
          }
          // Then check logo_url
          else if (b.logo_url && b.logo_url.trim() !== '') {
            businessImage = resolveAssetUrl(b.logo_url);
          }
          // Then check deal image
          else if (firstDeal?.image && firstDeal.image.trim() !== '') {
            businessImage = resolveAssetUrl(firstDeal.image);
          }
          
          // Fallback images based on category
          const fallbackImages = {
            'Food & Dining': 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=600&q=80',
            'Coffee & Bakery': 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=600&q=80',
            'Beauty & Wellness': 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=600&q=80',
            'Fitness': 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=600&q=80',
            'Auto': 'https://images.unsplash.com/photo-1486006920555-c77dcf18193c?w=600&q=80',
            'Retail & Shopping': 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&q=80',
            'Restaurant': 'https://images.unsplash.com/photo-1552566626-52f8b828add9?w=600&q=80',
            'default': 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&q=80'
          };
          
          if (!businessImage) {
            businessImage = fallbackImages[b.category] || fallbackImages['default'];
          }
          
          return {
            id: b.id,
            name: b.name,
            slug: b.slug,
            category: b.category || 'Local Business',
            subcategory: b.subcategory || null,
            description: b.tagline || b.description || '',
            image: businessImage,
            deals: b.deals || [],
            dealCount: b.deals?.length || 0,
            hubIds: b.hub_ids || [],
            // BR-13 / PD-11: only real numbers. null = "don't show".
            claimed: (b.deals || []).reduce((sum, d) => sum + (d.total_claims || 0), 0) || null,
            rating: b.google_rating || null,
            totalReviews: b.google_total_reviews || 0,
            hasGoogleRating: !!b.google_rating,
            distance: null,
            offerTag: firstDeal?.offer_type || 'DEAL',
            offerDescription: firstDeal?.description || b.tagline || 'Special offer available'
          };
        });
        
        setBusinesses(mappedBusinesses);
      } catch (error) {
        console.error('Error fetching businesses:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBusinesses();
  }, []);

  // Phase 1 (ADR-0009, PD-12): the hub unlock gate is dropped — every venue's
  // deals are visible, so no locked hub card and no `isHubUnlocked` branch.

  const canViewBusiness = (business) => {
    // Preview mode: show deals for the preview hub only
    if (isPreviewMode && previewHubId) {
      if (!business.hubIds || business.hubIds.length === 0) return true;
      return business.hubIds.some(hubId => String(hubId) === String(previewHubId));
    }
    // Phase 1: no gate outside preview mode
    return true;
  };

  // Category configuration - maps database values to display names and emojis
  const categoryConfig = {
    'All': { display: 'All', emoji: '✨' },
    // New categories
    'Food': { display: 'Food', emoji: '🍔' },
    'Beauty': { display: 'Beauty', emoji: '💇' },
    'Professional Services': { display: 'Business', emoji: '💼' },
    'Retail': { display: 'Retail', emoji: '🛍️' },
    'Wellness': { display: 'Wellness', emoji: '💪' },
    'Garden & Outdoor': { display: 'Garden', emoji: '🌱' },
    'Fun & Entertainment': { display: 'Fun', emoji: '🎯' },
    'Tech': { display: 'Tech', emoji: '💻' },
    'Auto': { display: 'Auto', emoji: '🚗' },
    'Local Services': { display: 'Services', emoji: '🔧' },
    // Legacy categories (for backwards compatibility)
    'Food & Drink': { display: 'Food', emoji: '🍔', mapTo: 'Food' },
    'Personal Services': { display: 'Beauty', emoji: '💇', mapTo: 'Beauty' },
    'Health & Wellness': { display: 'Wellness', emoji: '💪', mapTo: 'Wellness' },
    'Entertainment': { display: 'Fun', emoji: '🎯', mapTo: 'Fun & Entertainment' },
    'Arts & Culture': { display: 'Fun', emoji: '🎭', mapTo: 'Fun & Entertainment' },
    'Home & Garden': { display: 'Garden', emoji: '🌱', mapTo: 'Garden & Outdoor' },
    'Sports & Recreation': { display: 'Fun', emoji: '⚽', mapTo: 'Fun & Entertainment' },
    'Automotive': { display: 'Auto', emoji: '🚗', mapTo: 'Auto' },
    'Auto Repair / Service Center': { display: 'Auto', emoji: '🚗', mapTo: 'Auto' },
    'Garden Center / Landscaping Supplies': { display: 'Garden', emoji: '🌱', mapTo: 'Garden & Outdoor' },
    'Technology / App Development': { display: 'Tech', emoji: '💻', mapTo: 'Tech' },
  };
  const defaultConfig = { display: 'Other', emoji: '🏪' };

  const getEmoji = (category) => (categoryConfig[category] || defaultConfig).emoji;
  const getDisplayName = (category) => (categoryConfig[category] || defaultConfig).display;
  
  // Map legacy categories to new categories for filtering
  const getNormalizedCategory = (category) => {
    const config = categoryConfig[category];
    return config?.mapTo || category;
  };

  // Auto categories group
  const autoCategories = ['Automotive', 'Auto Repair / Service Center'];

  // Filter businesses based on category and selected hub
  const filteredBusinesses = businesses.filter(business => {
    // Normalize business category to handle legacy categories
    const normalizedBusinessCategory = getNormalizedCategory(business.category);
    const normalizedSelectedCategory = getNormalizedCategory(selectedCategory);
    
    let matchesCategory = selectedCategory === 'All' || 
                          normalizedBusinessCategory === normalizedSelectedCategory ||
                          business.category === selectedCategory;
    
    // Handle combined "Auto" filter (includes legacy "Automotive" etc.)
    if (selectedCategory === 'Auto') {
      matchesCategory = autoCategories.includes(business.category) || 
                        getNormalizedCategory(business.category) === 'Auto';
    }
    const matchesHub = !selectedHub || (business.hubIds && business.hubIds.includes(selectedHub));
    return matchesCategory && matchesHub;
  });

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(ITEMS_PER_PAGE);
  }, [selectedCategory, selectedHub]);

  const handleLoadMore = () => {
    setVisibleCount(prev => prev + ITEMS_PER_PAGE);
  };

  const handleClaimDeal = (business, e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!canViewBusiness(business)) {
      setNotification({
        type: 'locked',
        message: 'Sign up free to claim this deal.'
      });
      setTimeout(() => setNotification(null), 3000);
      return;
    }

    // Preview mode: Store pending claim and open auth modal
    if (isPreviewMode && !isAuthenticated) {
      // Get the first deal from this business
      const deal = business.deals?.[0];
      if (deal) {
        const pendingClaimData = {
          business_id: business.id,
          deal_id: deal.id,
          business_name: business.name,
          business_slug: business.slug,
          deal_title: deal.title,
          deal_description: deal.description
        };
        sessionStorage.setItem('pendingClaim', JSON.stringify(pendingClaimData));
      }
      // Set redirect to come back to this page with hub selected
      setPostAuthRedirect(`/local-deals-near-me?hub_id=${previewHubId}`);
      openAuthModal('signup');
      return;
    }

    if (!isAuthenticated) {
      openAuthModal('login');
      return;
    }

    navigate(`/business/${business.slug}`);
  };

  const handleHubClick = (hubId) => {
    if (selectedHub === hubId) {
      setSelectedHub(null); // Deselect if clicking the same hub
    } else {
      setSelectedHub(hubId);
    }
  };

  // Featured deal logic:
  // 1. If there's a backend featured deal (from selected hub OR any unlocked hub) - use that
  // 2. Otherwise, use automatic selection (first business with deals that user can view)
  let featuredBusiness = null;
  let isBackendFeaturedDeal = false;
  let featuredHubName = null;
  
  if (featuredDeal && featuredDeal.featured) {
    // Use backend featured deal (works for both selected hub and unselected hub cases)
    const { business, deal, hubName } = featuredDeal;
    featuredBusiness = {
      id: business.id,
      name: business.name,
      slug: business.slug,
      category: business.category || 'Local Business',
      image: business.image_url || business.logo_url || 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&q=80',
      rating: business.google_rating || 0,
      totalReviews: business.google_total_reviews || 0,
      hasGoogleRating: !!business.google_rating,
      offerTag: deal.title,
      offerDescription: deal.description || deal.title,
      claimed: deal.total_claims || 0,
      distance: null,
      dealCount: 1,
      hubIds: featuredDeal.hubId ? [featuredDeal.hubId] : (selectedHub ? [selectedHub] : [])
    };
    isBackendFeaturedDeal = true;
    featuredHubName = hubName;
  } else {
    // Automatic selection - first business with deals that user can view
    featuredBusiness = filteredBusinesses.find(b => b.dealCount > 0 && canViewBusiness(b));
  }
  
  // Deals to show (excluding featured if it's from the same list)
  const dealsToShow = isBackendFeaturedDeal 
    ? filteredBusinesses.filter(b => b.id !== featuredBusiness?.id) // Exclude backend featured from grid
    : filteredBusinesses.filter(b => b !== featuredBusiness);
  const visibleDeals = dealsToShow.slice(0, visibleCount);
  const hasMoreDeals = visibleCount < dealsToShow.length;

  return (
    // No `overflow-x-hidden` on this root: <html> already carries it, and an
    // overflow container on an ancestor disables the sticky category bar below.
    <div style={{ backgroundColor: '#FEF7ED' }} className="min-h-screen-dynamic text-[#1e1b4b]">
      <SEO 
        title="Local Deals Near You"
        description="Discover local deals and offers from businesses near you. Scan a Tappd coaster at any partner bar, claim deals, and save at local shops and restaurants."
        url="/local-deals-near-me"
        keywords="local deals, nearby offers, Kansas City deals, local discounts, restaurant deals, bar deals, local savings"
      />

      {/* Unlock Celebration Overlay */}
      <AnimatePresence>
      </AnimatePresence>

      {/* Notification Modal */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -50, scale: 0.9 }}
            className="fixed top-24 left-1/2 transform -translate-x-1/2 z-50 px-6 py-4 rounded-2xl shadow-2xl"
            style={{
              backgroundColor: notification.type === 'locked' ? '#1e1b4b' :
                notification.type === 'taps' ? undefined : '#10B981',
              background: notification.type === 'taps' ? 'linear-gradient(135deg, #8b5cf6 0%, #f97316 100%)' : undefined,
              boxShadow: notification.type === 'taps' ? '0 8px 32px rgba(139, 92, 246, 0.4)' : undefined
            }}
          >
            <p className="text-white font-bold flex items-center gap-2" style={{
              fontSize: notification.type === 'taps' ? '18px' : undefined
            }}>
              {notification.type === 'locked' ? <Lock size={18} /> :
                notification.type === 'taps' ? <Star size={20} className="text-yellow-300" /> : <Unlock size={18} />}
              {notification.message}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      <section className="relative pb-20" style={{ paddingTop: '8rem' }}>
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#FEF7ED] via-[#FDEBD0] to-[#F5D0C5]"></div>
          <div className="absolute top-10 right-1/4 w-[250px] h-[250px] md:w-[500px] md:h-[500px] bg-gradient-to-br from-orange-200/40 to-amber-100/30 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-[200px] h-[200px] md:w-[400px] md:h-[400px] bg-gradient-to-tr from-rose-200/30 to-orange-100/20 rounded-full blur-3xl"></div>
        </div>
        
        {/* V5 Gradient Line at Bottom */}
        <div className="absolute bottom-0 left-0 w-full h-1 z-20" style={{ background: 'linear-gradient(to right, #8b5cf6 0%, #f97316 100%)' }}></div>
        
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* V5 Code Accent */}
            <p className="text-xs mb-4" style={{ fontFamily: "'JetBrains Mono', monospace", color: 'rgba(30, 27, 75, 0.3)' }}>// local_deals</p>
            
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/90 border border-orange-100 text-sm shadow-sm mb-8">
              <span className="text-orange-500">✨</span>
              <span style={{ color: '#1e1b4b' }}>Local Deals</span>
            </div>
            
            {/* Headline */}
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-tight mb-2" style={{ fontFamily: "'Faustina', Georgia, serif", fontWeight: 500 }}>
              <span style={{ color: '#1e1b4b' }}>Deals Near You.</span>
            </h1>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-tight mb-8" style={{ fontFamily: "'Faustina', Georgia, serif", fontWeight: 500 }}>
              <span style={{ 
                background: 'linear-gradient(135deg, #ea580c 0%, #f97316 50%, #ea580c 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Scan. Claim. Save.</span>
            </h2>
            
            {/* Subheadline */}
            <p className="text-xl max-w-2xl mx-auto leading-relaxed mb-12" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
              Scan a Tappd coaster at any participating location to see the deals at that venue from local businesses
            </p>
            
            {/* Venue Cards - Show all hubs, responsive grid */}
            <div className={`grid gap-6 max-w-5xl mx-auto ${hubs.length <= 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
              {hubs.map((hub) => {
                const isSelected = selectedHub === hub.id;

                return (
                  <motion.div
                    key={hub.id}
                    className={`bg-white rounded-2xl p-6 border shadow-lg text-left cursor-pointer ${
                      isSelected ? 'border-emerald-500 ring-2 ring-emerald-200' : 'border-gray-100'
                    }`}
                    style={{ transition: 'transform 0.3s ease, box-shadow 0.3s ease' }}
                    whileHover={{ y: -6, boxShadow: '0 16px 48px rgba(234, 88, 12, 0.15)' }}
                    onClick={() => handleHubClick(hub.id)}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2 text-sm" style={{ color: 'rgba(30, 27, 75, 0.6)' }}>
                        <MapPin size={16} aria-hidden="true" />
                        {hub.city}, {hub.state}
                      </div>
                      <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                        <Unlock size={20} className="text-emerald-500" aria-hidden="true" />
                      </div>
                    </div>
                    <h3 className="font-bold text-xl mb-3" style={{ color: '#1e1b4b' }}>{hub.name}</h3>
                    <div className="bg-emerald-50 rounded-lg px-4 py-3 mb-3">
                      <span className="text-emerald-600 font-semibold">{hub.deal_count || 0} deals waiting</span>
                    </div>
                    <div className="flex items-center gap-2 text-emerald-500 text-sm font-medium">
                      <Unlock size={16} aria-hidden="true" />
                      {isSelected ? 'Showing deals from this venue' : 'Tap to filter'}
                    </div>
                  </motion.div>
                );
              })}
            </div>
            
            {/* Clear filter button */}
            {selectedHub && (
              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 px-6 py-2 rounded-full text-sm font-medium bg-white border border-orange-200 hover:border-orange-400 transition-all"
                style={{ color: '#1e1b4b' }}
                onClick={() => setSelectedHub(null)}
              >
                Clear venue filter ✕
              </motion.button>
            )}
          </motion.div>
        </div>
      </section>

      {/* Category Filter — one sticky bar. This used to swap a 60px placeholder
          for a `fixed top-0` copy with a 100px buffer, which jumped ~8px at the
          swap and assumed a desktop-height header (audit row 87). */}
      <div className="sticky top-24 z-40 py-4 bg-white/95 backdrop-blur-md border-b-2 border-orange-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-2 overflow-x-auto py-1 hide-scrollbar" role="group" aria-label="Filter deals by category">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                aria-pressed={selectedCategory === category}
                className={`px-4 py-2 min-h-[44px] rounded-full text-sm font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 ${
                  selectedCategory === category
                    ? 'bg-orange-500 text-white shadow-md'
                    : 'bg-orange-50 border border-orange-200 hover:border-orange-400 hover:bg-orange-100'
                }`}
                style={{ color: selectedCategory === category ? '#fff' : '#1e1b4b' }}
              >
                <span aria-hidden="true">{getEmoji(category)}</span>
                <span>{getDisplayName(category)}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Deals Grid */}
      <section id="deals" className="py-16 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://iolab.co/wp-content/uploads/2025/12/EMAIL-BG-4.png" alt="" className="w-full h-full object-cover opacity-10" />
        </div>
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(254, 247, 237, 0.8)' }}></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          {/* Featured Deal */}
          {featuredBusiness && (
            <motion.div 
              className="mb-12"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Featured label - enhanced when venue-selected */}
              {isBackendFeaturedDeal ? (
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full shadow-md">
                    <Star size={16} className="text-white fill-white" />
                    <span className="text-white text-sm font-bold">Venue Featured Deal</span>
                  </div>
                  <span className="text-sm text-gray-500">Selected by {featuredHubName || 'Partner Venue'}</span>
                </div>
              ) : (
                <div className="text-orange-600 text-sm font-semibold uppercase tracking-widest mb-4">⭐ Featured Deal</div>
              )}
              <Link to={`/business/${featuredBusiness.slug}`}>
                <div 
                  className={`bg-white rounded-3xl overflow-hidden shadow-xl ${
                    isBackendFeaturedDeal 
                      ? 'border-2 border-amber-300 ring-2 ring-amber-100' 
                      : 'border border-orange-200'
                  }`}
                  style={{ transition: 'transform 0.3s ease, box-shadow 0.3s ease' }}
                >
                  <div className="grid md:grid-cols-2">
                    <div className="relative h-48 sm:h-64 md:h-auto md:min-h-[300px]">
                      <img src={featuredBusiness.image} alt={featuredBusiness.name} className="w-full h-full object-cover" />
                      <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-orange-500 text-white text-xs font-bold">
                        {featuredBusiness.offerTag}
                      </div>
                      {featuredBusiness.hasGoogleRating && (
                      <div className="absolute bottom-4 left-4 flex items-center gap-1.5 bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-lg">
                        <img src="/google-reviews-icon.png" alt="Google" className="w-4 h-4" />
                        <Star size={16} className="text-yellow-400 fill-yellow-400" />
                        <span className="text-white text-sm font-medium">{Number(featuredBusiness.rating).toFixed(1)}</span>
                        {featuredBusiness.totalReviews > 0 && (
                          <span className="text-white/70 text-xs">({featuredBusiness.totalReviews.toLocaleString()})</span>
                        )}
                      </div>
                      )}
                    </div>
                    <div className="p-4 sm:p-6 md:p-8 flex flex-col justify-center">
                      <div className="text-sm text-orange-500 font-medium mb-2">{getEmoji(featuredBusiness.category)} {featuredBusiness.category}</div>
                      <h3 className="text-xl sm:text-2xl md:text-3xl mb-3" style={{ fontFamily: "'Playfair Display', serif", color: '#1e1b4b' }}>{featuredBusiness.name}</h3>
                      <p className="mb-4 leading-relaxed text-sm sm:text-base" style={{ color: 'rgba(30, 27, 75, 0.6)' }}>{featuredBusiness.offerDescription}</p>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm mb-4 sm:mb-6" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                        {featuredBusiness.distance != null && (<div className="flex items-center gap-1"><MapPin size={14} className="sm:w-4 sm:h-4" />{featuredBusiness.distance} mi</div>)}
                        {featuredBusiness.claimed != null && (<div className="flex items-center gap-1"><Users size={14} className="sm:w-4 sm:h-4" />{featuredBusiness.claimed} claimed</div>)}
                      </div>
                      <div className="flex items-center gap-4">
                        <button 
                          onClick={(e) => handleClaimDeal(featuredBusiness, e)}
                          className="flex-1 py-3 rounded-full font-semibold text-white transition-all shadow-lg flex items-center justify-center gap-2"
                          style={{ background: 'linear-gradient(to right, #f97316, #ea580c)', boxShadow: '0 10px 25px rgba(249, 115, 22, 0.25)' }}
                        >
                          Claim Deal
                          {flag('REWARDS') && <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-bold">+25 Taps</span>}
                        </button>
                        <button className="w-12 h-12 rounded-full border border-orange-200 flex items-center justify-center transition-all hover:text-orange-500 hover:border-orange-400" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                          <Heart size={20} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          )}

          {/* All Deals Grid Header */}
          <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h2 className="text-xl sm:text-2xl" style={{ fontFamily: "'Playfair Display', serif", color: '#1e1b4b' }}>
              {selectedHub ? `Deals from ${hubs.find(h => h.id === selectedHub)?.name || 'Selected Venue'}` : 'All Deals Near You'}
            </h2>
            <div className="flex items-center gap-2 text-sm" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
              <span>Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-white border border-orange-200 rounded-lg px-3 py-1.5 outline-none focus:border-orange-400"
              >
                <option value="distance">Distance</option>
                <option value="popular">Most Popular</option>
                <option value="ending">Ending Soon</option>
                <option value="newest">Newest</option>
              </select>
            </div>
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent"></div>
            </div>
          ) : (
            <>
              {/* Deal Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 w-full" style={{ gridAutoRows: '1fr' }}>
                <AnimatePresence>
                  {visibleDeals.map((business, index) => {
                    const isUnlocked = canViewBusiness(business);
                    
                    return (
                      <motion.div
                        key={business.id}
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.4, delay: (index % ITEMS_PER_PAGE) * 0.1 }}
                      >
                        <Link to={isUnlocked ? `/business/${business.slug}` : '#'} onClick={(e) => !isUnlocked && e.preventDefault()} className="block w-full">
                          <div
                            className={`bg-white rounded-2xl overflow-hidden border shadow-sm h-full flex flex-col w-full ${isUnlocked ? 'border-orange-100' : 'border-gray-200 opacity-75'}`}
                            style={{ transition: 'transform 0.3s ease, box-shadow 0.3s ease' }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-6px)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                          >
                            {/* Image - Responsive Height */}
                            <div className="relative h-36 sm:h-44 flex-shrink-0">
                              <img 
                                src={business.image} 
                                alt={business.name} 
                                className={`w-full h-full object-cover ${!isUnlocked ? 'filter blur-sm' : ''}`}
                                onError={(e) => {
                                  e.target.src = 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&q=80';
                                }}
                              />
                              <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-white text-xs font-bold" style={{ backgroundColor: '#f97316' }}>
                                {business.offerTag}
                              </div>
                              <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center">
                                {isUnlocked ? (
                                  <Heart size={16} style={{ color: 'rgba(30, 27, 75, 0.5)' }} />
                                ) : (
                                  <Lock size={16} className="text-orange-500" />
                                )}
                              </div>
                            </div>
                            {/* Content - Flex grow to fill remaining space */}
                            <div className="p-4 sm:p-5 flex flex-col flex-grow">
                              {/* Category & Rating Row - Fixed Height */}
                              <div className="flex items-center justify-between mb-2 h-6">
                                <div className="flex items-center gap-2 overflow-hidden">
                                  <span className="text-xs text-orange-500 font-medium whitespace-nowrap">{getEmoji(business.category)} {business.category}</span>
                                </div>
                                {business.hasGoogleRating && (
                                <div className="flex items-center gap-1 text-xs flex-shrink-0" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                                  <img src="/google-reviews-icon.png" alt="Google" className="w-3.5 h-3.5" />
                                  <Star size={12} className="text-yellow-400 fill-yellow-400" />
                                  <span>{Number(business.rating).toFixed(1)}</span>
                                </div>
                                )}
                              </div>
                              {/* Business Name - Single Line Truncate */}
                              <h3 className="font-semibold text-base sm:text-lg mb-1 truncate" style={{ color: '#1e1b4b' }} title={business.name}>{business.name}</h3>
                              {/* Offer Description - 2 Lines Max */}
                              <p className="text-xs sm:text-sm mb-3 line-clamp-2 flex-grow" style={{ color: 'rgba(30, 27, 75, 0.6)' }}>{business.offerDescription}</p>
                              {/* Footer - Always at bottom */}
                              <div className="flex items-center justify-between mt-auto gap-2">
                                <div className="flex items-center gap-2 sm:gap-3 text-xs" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                                  {business.distance != null && <span>{business.distance} mi</span>}
                                  {business.claimed != null && <span>{business.claimed} claimed</span>}
                                </div>
                                <button
                                  onClick={(e) => handleClaimDeal(business, e)}
                                  className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold transition-all flex-shrink-0 flex items-center gap-1"
                                  style={{
                                    backgroundColor: isUnlocked
                                      ? (isPreviewMode ? 'rgba(139, 92, 246, 0.1)' : 'rgba(255, 237, 213, 1)')
                                      : 'rgba(30, 27, 75, 0.1)',
                                    color: isUnlocked
                                      ? (isPreviewMode ? '#8b5cf6' : '#ea580c')
                                      : 'rgba(30, 27, 75, 0.5)'
                                  }}
                                >
                                  
                                  {isUnlocked ? 'Claim' : 'Locked'}
                                </button>
                              </div>
                            </div>
                          </div>
                        </Link>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>

              {/* Empty State */}
              {filteredBusinesses.length === 0 && (
                <div className="text-center py-20">
                  <div className="text-6xl mb-4">🔍</div>
                  <h3 className="text-2xl font-semibold mb-2" style={{ color: '#1e1b4b' }}>No deals found</h3>
                  <p style={{ color: 'rgba(30, 27, 75, 0.6)' }}>Try adjusting your filters or check back later</p>
                </div>
              )}

              {/* Load More Button */}
              {hasMoreDeals && (
                <motion.div 
                  className="text-center mt-12"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <button 
                    onClick={handleLoadMore}
                    className="px-8 py-3 border-2 border-orange-300 rounded-full font-semibold text-orange-600 hover:bg-orange-50 transition-all"
                  >
                    Load More Deals ({dealsToShow.length - visibleCount} remaining)
                  </button>
                </motion.div>
              )}
            </>
          )}
        </div>
      </section>

      {/* How to Claim Section */}
      <section className="py-24 relative overflow-hidden border-t-4 border-b-4 border-white">
        <div className="absolute inset-0">
          <img src="https://iolab.co/wp-content/uploads/2025/12/EMAIL-BG-5.png" alt="" className="w-full h-full object-cover opacity-40" />
        </div>
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(30, 27, 75, 0.9)' }}></div>
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <div className="text-orange-400 text-sm font-semibold uppercase tracking-widest mb-4">How It Works</div>
            <h2 className="text-4xl md:text-5xl text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Claim deals in 3 easy steps.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl mx-auto mb-6" style={{ backgroundColor: 'rgba(249, 115, 22, 0.2)' }}>📱</div>
              <h3 className="text-xl font-semibold mb-3 text-white">1. Scan the Coaster</h3>
              <p className="text-white/60">At any partner bar, scan the QR code on your coaster with your phone camera.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl mx-auto mb-6" style={{ backgroundColor: 'rgba(249, 115, 22, 0.2)' }}>🎟️</div>
              <h3 className="text-xl font-semibold mb-3 text-white">2. Claim Your Deal</h3>
              <p className="text-white/60">Browse available offers and tap to claim. No app download required.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl mx-auto mb-6" style={{ backgroundColor: 'rgba(249, 115, 22, 0.2)' }}>🎉</div>
              <h3 className="text-xl font-semibold mb-3 text-white">3. Visit & Save</h3>
              <p className="text-white/60">Show your claimed offer at the business and enjoy your discount!</p>
            </div>
          </div>
        </div>
      </section>

      {/* Preview Mode Banner — fixed bottom */}
      {isPreviewMode && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed bottom-0 left-0 right-0 z-40 px-4 pb-4"
        >
          <div
            className="max-w-4xl mx-auto px-4 sm:px-6 py-4 rounded-2xl shadow-lg text-center"
            style={{
              background: 'linear-gradient(135deg, #1e1b4b 0%, #2d2a5d 100%)',
              border: '1px solid rgba(139, 92, 246, 0.3)'
            }}
          >
            <p className="text-white text-sm mb-3">
              {flag('REWARDS') ? (<>Sign up free and collect <span className="text-orange-400 font-bold">150 bonus Taps</span></>) : (<>Sign up free to claim deals at this venue</>)}
            </p>
            <button
              onClick={() => openAuthModal('signup')}
              className="relative overflow-hidden px-6 py-2.5 rounded-xl font-bold text-sm transition-all hover:-translate-y-0.5"
              style={{
                background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                color: 'white',
                boxShadow: '0 4px 15px rgba(249, 115, 22, 0.4)'
              }}
            >
              <span className="relative z-10">{flag('REWARDS') ? '🎁 Claim 150 Free Taps' : 'Sign up free'}</span>
              <span className="animate-shimmer absolute inset-0 rounded-xl" />
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default Businesses;
